import React, { forwardRef, useRef, useEffect, useImperativeHandle } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

const PinchZoomContainer = forwardRef(function PinchZoomContainer(
  {
    imageDimensions = { width: '100%', height: '100%' },
    minScale = 1,
    maxScale = 4,
    doubleTapScale = 2, // zoom step on toggle-in
    resetKey = null,
    className,
    style = {},
    children,
    ...rest
  },
  ref
) {
  const transformApiRef = useRef(null);

  // for mobile double-tap detection
  const lastTapTimeRef = useRef(0);
  const lastTapPosRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (transformApiRef.current?.resetTransform) {
      transformApiRef.current.resetTransform();
    }
  }, [resetKey]);

  useImperativeHandle(
    ref,
    () => ({
      reset: () => transformApiRef.current?.resetTransform(),
      zoomIn: (step = 1) => transformApiRef.current?.zoomIn(step),
      zoomOut: (step = 1) => transformApiRef.current?.zoomOut(step),
      getApi: () => transformApiRef.current,
    }),
    []
  );

  const normalizeDim = (v) => (typeof v === 'number' ? `${v}px` : v || 'auto');

  const wrapperStyle = {
    touchAction: 'none', // allow pinch/pan
    width: normalizeDim(imageDimensions.width),
    height: normalizeDim(imageDimensions.height),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    ...style,
  };

  const contentStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 'auto',
    height: 'auto',
  };

  const toggleZoom = () => {
    const api = transformApiRef.current;
    if (!api?.instance) return;
    const { scale } = api.instance.transformState;
    if (scale > 1)
      api.resetTransform(); // zoom out to 1x
    else api.zoomIn(doubleTapScale); // zoom in by step
  };

  const handleTouchEnd = (e) => {
    // Only consider single-finger taps (not pinches)
    if (e.changedTouches.length !== 1) return;

    const t = e.changedTouches[0];
    const now = Date.now();
    const dt = now - lastTapTimeRef.current;

    // distance threshold (to filter drags) ~20px
    const dx = t.clientX - lastTapPosRef.current.x;
    const dy = t.clientY - lastTapPosRef.current.y;
    const dist2 = dx * dx + dy * dy;

    if (dt < 300 && dist2 < 400) {
      // 300ms + ~20px
      toggleZoom(); // double tap detected
      lastTapTimeRef.current = 0; // reset
    } else {
      lastTapTimeRef.current = now;
      lastTapPosRef.current = { x: t.clientX, y: t.clientY };
    }
  };

  return (
    <TransformWrapper
      key={resetKey}
      onInit={(api) => {
        transformApiRef.current = api;
      }}
      initialScale={1}
      minScale={minScale}
      maxScale={maxScale}
      centerOnInit={false}
      limitToBounds={true}
      pinch={{ disabled: false, step: 5 }}
      doubleClick={{ disabled: true }} /* disable built-in to use our toggle */
      wheel={{ disabled: true }} /* enable if you want wheel zoom on desktop */
      wrapperStyle={wrapperStyle}
      contentStyle={contentStyle}
      className={className}
      {...rest}
    >
      <TransformComponent>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: `${imageDimensions.width}`,
            height: `${imageDimensions.height}`,
            pointerEvents: 'auto', // allow clicks/taps to register
          }}
          onDoubleClick={toggleZoom} // desktop double-click toggle
          onTouchEnd={handleTouchEnd} // mobile double-tap toggle
        >
          {children /* tip: set pointerEvents:'none' on the <img> itself if needed */}
        </div>
      </TransformComponent>
    </TransformWrapper>
  );
});

export default PinchZoomContainer;
